"use client"

import React from 'react';
import { NavigationProps } from "@/types/navigation";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { Check, CreditCard, Receipt, Zap } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface BillingPageProps extends NavigationProps {}

export default function BillingPage({ onNavigate }: BillingPageProps) {
  const subscriptionPlans = [
    {
      name: "Free",
      price: "$0",
      description: "Perfect for getting started",
      features: [
        "Basic expense tracking",
        "Up to 2 budgets",
        "Monthly reports",
        "Email support"
      ]
    },
    {
      name: "Pro",
      price: "$9.99",
      period: "month",
      description: "Best for active budgeters",
      features: [
        "Everything in Free",
        "Unlimited budgets",
        "Custom categories",
        "Priority support",
        "Advanced analytics",
        "Export data"
      ]
    },
    {
      name: "Business",
      price: "$19.99",
      period: "month",
      description: "For teams and businesses",
      features: [
        "Everything in Pro",
        "Team collaboration",
        "Role management",
        "API access",
        "Custom branding",
        "Dedicated support"
      ]
    }
  ];

  const recentTransactions = [
    {
      date: "2024-03-15",
      description: "Pro Plan - Monthly",
      amount: "$9.99",
      status: "Paid"
    },
    {
      date: "2024-02-15",
      description: "Pro Plan - Monthly",
      amount: "$9.99",
      status: "Paid"
    },
    {
      date: "2024-01-15",
      description: "Pro Plan - Monthly",
      amount: "$9.99",
      status: "Paid"
    }
  ];

  return (
    <>
      <PageHeader 
        onNavigate={onNavigate}
        currentPage="Billing"
        showDashboardLink={true}
      />

      <main className="flex-1 overflow-auto p-3 sm:p-4 md:p-6">
        <div className="mb-4 md:mb-6">
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Billing & Subscription</h1>
          <p className="text-sm sm:text-base text-muted-foreground">Manage your subscription and billing information</p>
        </div>

        {/* Current Plan Summary */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Zap className="h-5 w-5 text-primary" />
              Current Plan
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h3 className="font-semibold">Pro Plan</h3>
                <p className="text-sm text-muted-foreground">Your next billing date is April 15, 2024</p>
              </div>
              <Button variant="outline">
                <CreditCard className="mr-2 h-4 w-4" />
                Update Payment Method
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Subscription Plans */}
        <div className="grid gap-6 md:grid-cols-3 mb-6">
          {subscriptionPlans.map((plan) => (
            <Card key={plan.name} className="flex flex-col">
              <CardHeader>
                <CardTitle>{plan.name}</CardTitle>
                <CardDescription>{plan.description}</CardDescription>
              </CardHeader>
              <CardContent className="flex-1">
                <div className="mb-4">
                  <span className="text-3xl font-bold">{plan.price}</span>
                  {plan.period && <span className="text-muted-foreground">/{plan.period}</span>}
                </div>
                <ul className="space-y-2">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-primary" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter>
                <Button className="w-full" variant={plan.name === "Pro" ? "default" : "outline"}>
                  {plan.name === "Pro" ? "Current Plan" : `Upgrade to ${plan.name}`}
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>

        {/* Payment History */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Receipt className="h-5 w-5 text-muted-foreground" />
              Payment History
            </CardTitle>
            <CardDescription>Your recent subscription payments</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentTransactions.map((transaction, i) => (
                  <TableRow key={i}>
                    <TableCell>{transaction.date}</TableCell>
                    <TableCell>{transaction.description}</TableCell>
                    <TableCell>{transaction.amount}</TableCell>
                    <TableCell>{transaction.status}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </main>
    </>
  );
} 